import React, { Suspense, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  getAllCycles,
  GetEmployeeAppraisals,
  getSelfAppraisals,
  GetSkipLevelAppraisals,
} from "./PMSService";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { TostMsg } from "../../../App";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

const tabs = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "reviewed", label: "Reviewed" },
  { key: "sentback", label: "Sent Back" },
];

/* -------------------------------------------------------
   Helper Functions
------------------------------------------------------- */

const getInitials = (name = "") => {
  if (!name) return "--";

  const words = String(name).trim().split(/\s+/).filter(Boolean);

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

const formatDate = (date) => {
  if (!date) return "Pending";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const normalizeStatus = (status) => {
  if (!status) return "Pending Your Review";

  const value = String(status).trim().toLowerCase();

  if (value.includes("self assessment")) {
    return "Waiting for Manager";
  }

  if (
    value.includes("pending") &&
    (value.includes("review") ||
      value.includes("your") ||
      value.includes("skip"))
  ) {
    return "Pending Your Review";
  }

  if (
    value.includes("reviewed") ||
    value.includes("completed") ||
    value.includes("complete")
  ) {
    return "Reviewed";
  }

  if (
    value.includes("sent back") ||
    value.includes("sentback") ||
    value.includes("send back")
  ) {
    return "Sent Back";
  }

  if (value.includes("overdue")) {
    return "Overdue";
  }

  if (
    value.includes("waiting") ||
    value.includes("manager pending")
  ) {
    return "Waiting for Manager";
  }

  return status;
};

const getStatusBadgeClass = (status) => {
  const normalized = normalizeStatus(status);

  switch (normalized) {
    case "Reviewed":
      return "badge-completed";

    case "Overdue":
      return "badge-overdue";

    case "Waiting for Manager":
      return "badge-created";

    case "Sent Back":
      return "badge-overdue";

    case "Pending Your Review":
    default:
      return "badge-self";
  }
};


const getActionLabel = (status, isSkipLevelReviewed = false) => {
  // Once skip-level reviewer has completed the review,
  // always show View.
  if (isSkipLevelReviewed) {
    return "View";
  }

  const normalized = normalizeStatus(status);

  switch (normalized) {
    case "Waiting for Manager":
      return "Locked";

    case "Overdue":
      return "Review";

    case "Sent Back":
      return "Review";

    case "Pending Your Review":
    default:
      return "Review";
  }
}

const getActionIcon = (status, isSkipLevelReviewed = false) => {
  // Completed skip-level review
  if (isSkipLevelReviewed) {
    return "fa-eye";
  }

  const normalized = normalizeStatus(status);

  switch (normalized) {
    case "Waiting for Manager":
      return "fa-lock";

    default:
      return "fa-pen-to-square";
  }
};

const getActionVariant = (status, isSkipLevelReviewed = false) => {
  if (isSkipLevelReviewed) {
    return "secondary";
  }

  const normalized = normalizeStatus(status);

  if (normalized === "Waiting for Manager") {
    return "secondary";
  }

  return "primary";
};



// const getActionIcon = (status) => {
//   const normalized = normalizeStatus(status);

//   switch (normalized) {
//     case "Reviewed":
//       return "fa-eye";

//     case "Waiting for Manager":
//       return "fa-lock";

//     default:
//       return "fa-pen-to-square";
//   }
// };


// const getActionVariant = (status) => {
//   const normalized = normalizeStatus(status);

//   if (normalized === "Reviewed") {
//     return "secondary";
//   }

//   return "primary";
// };



const getActionStyle = (status) => {
  const normalized = normalizeStatus(status);

  if (normalized === "Overdue") {
    return {
      background: "#e53e3e",
    };
  }

  if (normalized === "Waiting for Manager") {
    return {
      opacity: 0.4,
      cursor: "not-allowed",
    };
  }

  return {};
};

/* -------------------------------------------------------
   Component
------------------------------------------------------- */

const PMSSkipLevelList = () => {
  const [selectedCycle, setSelectedCycle] = useState("");

  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("All Departments");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const history = useHistory();
  const { guid } = useParams();
  const { toster } = useContext(TostMsg);
  const [appraisals, setAppraisals] = useState([]);
  const [skipReviewSummary, setSkipReviewSummary] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(10);
const [totalRecords, setTotalRecords] = useState(0);

  /* -------------------------------------------------------
     Get Skip Level Appraisals
  ------------------------------------------------------- */

  useEffect(() => {
    fetchAppraisals();
    getAppraisalDetail();
  }, []);

useEffect(() => {
  if (selectedCycle) {
    setCurrentPage(1);
    fetchSkipLevelAppraisals(1, pageSize);
  } else {
    setRows([]);
    setTotalRecords(0);
  }
}, [selectedCycle]);

  const getAppraisalDetail = async () => {
    try {
      if (!guid) {
        console.error("Assessment GUID not found.");
        return null;
      }

      setLoading(true);
      const params = {
        Guid: guid,
      };

      const response = await GetEmployeeAppraisals(params);
      const responseRoot = response?.Data ?? response;

      setSelectedCycle(responseRoot?.CycleId);
      setLoading(false);
    } catch (error) {
      console.error("Get appraisal detail error:", error);

      toster?.current?.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Failed to load appraisal details.",
      );
 setLoading(false);
      
    }
  };

const fetchSkipLevelAppraisals = async (
  page = currentPage,
  size = pageSize
) => {
  if (!selectedCycle) {
    setRows([]);
    setTotalRecords(0);
    return;
  }

  const params = {
    CycleId: Number(selectedCycle),
    StageFilter: "",
    Search: "",
    PageNumber: page,
    PageSize: size,
  };

  try {
    setLoading(true);

    const response = await GetSkipLevelAppraisals(params);

    const responseData = response?.Data || {};

    const data = Array.isArray(responseData?.Items)
      ? responseData.Items
      : [];

    const summary = responseData?.Summary || {};

    setSkipReviewSummary(summary);

    // Use API total count
    setTotalRecords(
      Number(
        responseData?.TotalRecords ??
        responseData?.TotalCount ??
        summary?.Total ??
        data.length
      )
    );

    
const mappedRows = data.map((item) => {
  const employeeName = item?.EmployeeName || "Unknown Employee";
  const managerName = item?.ManagerName || "-";

  const selfSubmitted = item?.SelfSubmittedOn
    ? formatDate(item.SelfSubmittedOn)
    : "Pending";

  const managerReviewed = item?.ManagerReviewedOn
    ? formatDate(item.ManagerReviewedOn)
    : "Pending";

  let normalizedStatus = normalizeStatus(item?.StageName);

  // ---------------------------------------------------
  // Skip-Level Review Completed Check
  // ---------------------------------------------------
  const isSkipLevelReviewed =
    item?.IsSkipLevelReviewed === true ||
    item?.IsSkipLevelReviewed === 1 ||
    item?.SkipLevelReviewed === true ||
    item?.SkipLevelReviewed === 1 ||
    !!item?.SkipLevelReviewedOn ||
    !!item?.SkipLevelReviewCompletedOn;

  // ---------------------------------------------------
  // Overdue
  // ---------------------------------------------------
  if (item?.IsOverdue === 1 && !isSkipLevelReviewed) {
    normalizedStatus = "Overdue";
  }

  // ---------------------------------------------------
  // If skip-level review is completed,
  // status should be Reviewed.
  // ---------------------------------------------------
  if (isSkipLevelReviewed) {
    normalizedStatus = "Reviewed";
  }

  const score =
    item?.ComputedScore !== null &&
    item?.ComputedScore !== undefined &&
    item?.ComputedScore !== ""
      ? Number(item.ComputedScore).toFixed(1)
      : "—";

  const isWaitingForManager =
    normalizedStatus === "Waiting for Manager";

  const actionDisabled =
    isWaitingForManager || !item?.AppraisalGUID;

  return {
    id: item?.AppraisalId,
    appraisalId: item?.AppraisalId,
    appraisalGuid: item?.AppraisalGUID,
    employeeGuid: item?.EmployeeGUID,
    empId: item?.EmpId,

    name: employeeName,
    initials: getInitials(employeeName),
    role: item?.Designation || "N/A",
    department: item?.Department || "N/A",
    profileImage: item?.ProfileImage || "",

    managerName: item?.ManagerName,
    managerInitials: getInitials(managerName),

    selfSubmitted,
    managerReviewed,

    score: item?.ManagerFinalScore,

    // Status
    status: normalizedStatus,
    badgeClass: getStatusBadgeClass(normalizedStatus),

    // IMPORTANT:
    // Action is based on whether YOU have completed
    // the skip-level review.
    actionLabel: getActionLabel(
      normalizedStatus,
      isSkipLevelReviewed
    ),

    actionIcon: getActionIcon(
      normalizedStatus,
      isSkipLevelReviewed
    ),

    actionVariant: getActionVariant(
      normalizedStatus,
      isSkipLevelReviewed
    ),

    actionDisabled,
    actionStyle: getActionStyle(normalizedStatus),

    actionPath: item?.AppraisalGUID
      ? `/Assessment/Skiplevel/Review/${item.AppraisalGUID}`
      : null,

    isOverdue: item?.IsOverdue === 1,

    // NEW
    isSkipLevelReviewed,

    stageId: item?.StageId,
    stageName: item?.StageName,
  };
});



    setRows(mappedRows);
  } catch (error) {
    console.error("Error fetching skip-level appraisals:", error);
    setRows([]);
    setTotalRecords(0);
  } finally {
    setLoading(false);
  }
};

  const fetchAppraisals = async () => {
    setLoading(true);

    try {
      const response = await getAllCycles();

      const data = response?.Data || response?.result || [];

      setAppraisals(data);
      setSelectedCycle(data[0]?.Id);
    } catch (err) {
      console.error("getSelfAppraisals error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCycleChange = (e) => {
    const cycleId = e.target.value;

    setSelectedCycle(cycleId);
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

const startRecord =
  totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;

const endRecord = Math.min(
  currentPage * pageSize,
  totalRecords
);

const handlePreviousPage = () => {
  if (currentPage <= 1 || loading) return;

  const newPage = currentPage - 1;

  setCurrentPage(newPage);
  fetchSkipLevelAppraisals(newPage, pageSize);
};

const handleNextPage = () => {
  if (currentPage >= totalPages || loading) return;

  const newPage = currentPage + 1;

  setCurrentPage(newPage);
  fetchSkipLevelAppraisals(newPage, pageSize);
};

const handlePageSizeChange = (e) => {
  const newSize = Number(e.target.value);

  setPageSize(newSize);
  setCurrentPage(1);

  fetchSkipLevelAppraisals(1, newSize);
};

  /* -------------------------------------------------------
     Dynamic Department Options
  ------------------------------------------------------- */

  const departmentOptions = useMemo(() => {
    const departments = rows
      .map((row) => row.department)
      .filter(
        (department) =>
          department && department !== "-" && department !== "N/A",
      );

    return ["All Departments", ...Array.from(new Set(departments))];
  }, [rows]);

  /* -------------------------------------------------------
     Dynamic Tab Counts
  ------------------------------------------------------- */

  const tabCounts = useMemo(() => {
    return {
      all: rows.length,

      pending: rows.filter((row) => row.status === "Pending Your Review")
        .length,

      reviewed: rows.filter((row) => row.status === "Reviewed").length,

      sentback: rows.filter((row) => row.status === "Sent Back").length,
    };
  }, [rows]);

  /* -------------------------------------------------------
     Filter Rows
  ------------------------------------------------------- */

  const filteredRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesTab =
        activeTab === "all" ||
        (activeTab === "pending" && row.status === "Pending Your Review") ||
        (activeTab === "reviewed" && row.status === "Reviewed") ||
        (activeTab === "sentback" && row.status === "Sent Back");

      const matchesSearch =
        !search ||
        row.name.toLowerCase().includes(search) ||
        row.role.toLowerCase().includes(search) ||
        row.department.toLowerCase().includes(search);

      const matchesDepartment =
        departmentFilter === "All Departments" ||
        row.department === departmentFilter;

      return matchesTab && matchesSearch && matchesDepartment;
    });
  }, [rows, activeTab, searchTerm, departmentFilter]);

  /* -------------------------------------------------------
     Summary Stats
  ------------------------------------------------------- */

  const stats = useMemo(() => {
    return {
      total: rows.length,

      pendingReview: rows.filter((row) => row.status === "Pending Your Review")
        .length,

      reviewedByYou: rows.filter((row) => row.status === "Reviewed").length,

      overdue: rows.filter((row) => row.status === "Overdue").length,
    };
  }, [rows]);

  /* -------------------------------------------------------
     Open Appraisal
  ------------------------------------------------------- */

  const handleAction = (row) => {
    if (row.actionDisabled || !row.actionPath) {
      return;
    }

    history.push(row.actionPath);
  };

  /* -------------------------------------------------------
     Render
  ------------------------------------------------------- */

  return (
    <div className="container-fluid">
      <div className="row g-3 assessment-module">
        <div className="og-main">
          {/* -----------------------------------------------
              Header
          ------------------------------------------------ */}

          <div className="og-page-header">
            <div>
              <div className="og-breadcrumb">
                <a
                  href='/Assessment/Employee/Dashboard'
                  style={{
                    color: "#EC4119",
                    textDecoration: "none",
                  }}
                >
                  Performance
                </a>{" "}
                › Skip-Level Reviews
              </div>

              <div className="og-page-title">
                Team Appraisals — Skip-Level Review
              </div>

              <div className="og-page-sub">
                {selectedCycle} · Your skip-level review queue
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
              }}
            >
              <select
                className="og-select"
                style={{
                  width: 210,
                  fontSize: 12,
                }}
                value={selectedCycle}
                onChange={handleCycleChange}
              >
                {Array.isArray(appraisals) &&
                  appraisals.map((cycle) => (
                    <option
                      key={cycle?.Id}
                      value={cycle?.Id}
                    >
                      {cycle?.Title || "Untitled Cycle"}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* -----------------------------------------------
              Summary Strip
          ------------------------------------------------ */}

          <div
            className="og-kpi-grid"
            style={{
              gridTemplateColumns: "repeat(6,1fr)",
              marginBottom: 20,
            }}
          >
            <div className="og-kpi">
              <div className="og-kpi-label">Total to Review</div>

              <div className="og-kpi-value">{skipReviewSummary?.Total}</div>

              <div className="og-kpi-sub">In your queue</div>
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label">Self Pending</div>

              <div className="og-kpi-value">{skipReviewSummary?.SelfPending}</div>

              {/* <div className="og-kpi-sub">In your queue</div> */}
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label">Manager Review Pending</div>

              <div className="og-kpi-value">{skipReviewSummary?.ManagerReviewPending}</div>

              {/* <div className="og-kpi-sub">In your queue</div> */}
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label" style={{ color: "#d97706" }}>
                Pending Review
              </div>

              <div className="og-kpi-value" style={{ color: "#d97706" }}>
              {skipReviewSummary?.SkipLevelPending}
              </div>

              <div className="og-kpi-sub">Manager review done</div>
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label" style={{ color: "#276749" }}>
                Reviewed by You
              </div>

              <div className="og-kpi-value" style={{ color: "#276749" }}>
                {skipReviewSummary?.Completed}
              </div>

              <div className="og-kpi-sub">Sent to normalization</div>
            </div>

            <div className="og-kpi">
              <div className="og-kpi-label" style={{ color: "#9b2c2c" }}>
                Overdue
              </div>

              <div className="og-kpi-value" style={{ color: "#9b2c2c" }}>
               {skipReviewSummary?.Overdue}
              </div>

              <div className="og-kpi-sub">Past deadline</div>
            </div>
          </div>

          {/* -----------------------------------------------
              Main Card
          ------------------------------------------------ */}

          <div className="og-card">
            {/* ---------------------------------------------
                Filters
            ---------------------------------------------- */}

            <div
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 14,
                alignItems: "center",
              }}
            >
              {/* Tabs */}

              <div
                style={{
                  display: "flex",
                  gap: 0,
                  border: "1px solid #e9ecf0",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`btn-og ${
                      activeTab === tab.key
                        ? "btn-og-primary"
                        : "btn-og-secondary"
                    } btn-og-sm`}
                    style={{
                      borderRadius: 0,
                    }}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label} ({tabCounts[tab.key]})
                  </button>
                ))}
              </div>

              {/* Search */}

              <input
                className="og-input"
                type="text"
                placeholder="Search employee..."
                style={{ flex: 1 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {/* Department */}

              <select
                className="og-select"
                style={{ width: 180 }}
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                {departmentOptions.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            {/* ---------------------------------------------
                Table
            ---------------------------------------------- */}

            <div className="og-table-wrap">
              <table className="og-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Reporting Manager</th>
                    <th>Self Submitted</th>
                    <th>Manager Reviewed</th>
                    <th>Manager Score</th>
                    <th>Your Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {/* Loading */}

                  {loading && (
                    <tr>
                      <td
                        colSpan="8"
                        style={{
                          textAlign: "center",
                          padding: 30,
                        }}
                      >
                        Loading skip-level appraisals...
                      </td>
                    </tr>
                  )}

                  {/* Empty */}

                  {!loading && filteredRows.length === 0 && (
                    <tr>
                      <td
                        colSpan="8"
                        style={{
                          textAlign: "center",
                          padding: 30,
                          color: "#8492a6",
                        }}
                      >
                        No skip-level appraisals found.
                      </td>
                    </tr>
                  )}

                  {/* Rows */}

                  {!loading &&
                    filteredRows.map((row) => (
                      <tr key={row.id} style={row.rowStyle || {}}>
                        {/* Employee */}

                        <td>
                          <div className="og-emp-cell">
                            <div
                              className="og-emp-av"
                              style={{
                                background: "#EC4119",
                              }}
                            >
                              {row.initials}
                            </div>

                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                }}
                              >
                                {row.name}
                              </div>

                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#8492a6",
                                }}
                              >
                                {row.role}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Department */}

                        <td>{row.department}</td>

                        {/* Reporting Manager */}

                        <td>
                          <div className="og-emp-cell">
                            <div
                              className="og-emp-av"
                              style={{
                                background: "#2b6cb0",
                                width: 22,
                                height: 22,
                                fontSize: 9,
                              }}
                            >
                              {row.managerInitials}
                            </div>

                            <span
                              style={{
                                fontSize: 12,
                              }}
                            >
                              {row.managerName}
                            </span>
                          </div>
                        </td>

                        {/* Self Submitted */}

                        <td>
                          <span
                            style={{
                              fontSize: 12,
                              color:
                                row.selfSubmitted === "Pending"
                                  ? "#8492a6"
                                  : "#276749",
                            }}
                          >
                            {row.selfSubmitted !== "Pending" && (
                              <i className="fa fa-check"></i>
                            )}{" "}
                            {row.selfSubmitted}
                          </span>
                        </td>

                        {/* Manager Reviewed */}

                        <td>
                          <span
                            style={{
                              fontSize: 12,
                              color:
                                row.managerReviewed === "Pending"
                                  ? "#8492a6"
                                  : "#276749",
                            }}
                          >
                            {row.managerReviewed !== "Pending" && (
                              <i className="fa fa-check"></i>
                            )}{" "}
                            {row.managerReviewed}
                          </span>
                        </td>

                        {/* Manager Score */}

                        <td>
                          <strong>{row.score}</strong>

                          {row.score !== "—" && (
                            <span
                              style={{
                                color: "#8492a6",
                                fontSize: 11,
                              }}
                            >
                              /5
                            </span>
                          )}
                        </td>

                        {/* Status */}

                        <td>
                          <span className={`badge-og ${row.badgeClass}`}>
                            {row.status}
                          </span>
                        </td>

                        {/* Action */}

                        <td>
                          <button
                            className={`btn-og btn-og-${row.actionVariant} btn-og-sm`}
                            style={row.actionStyle}
                            onClick={() => handleAction(row)}
                            disabled={row.actionDisabled}
                          >
                            <i className={`fa ${row.actionIcon}`}></i>{" "}
                            {row.actionLabel}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 4px 2px",
    fontSize: 12,
  }}
>
  <div
    style={{
      color: "#8492a6",
    }}
  >
    Showing{" "}
    <strong style={{ color: "#2d3748" }}>
      {startRecord}
    </strong>{" "}
    to{" "}
    <strong style={{ color: "#2d3748" }}>
      {endRecord}
    </strong>{" "}
    of{" "}
    <strong style={{ color: "#2d3748" }}>
      {totalRecords}
    </strong>
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    {/* Page Size */}
    <select
      className="og-select"
      value={pageSize}
      onChange={handlePageSizeChange}
      style={{
        width: 70,
        fontSize: 12,
        height: 32,
      }}
      disabled={loading}
    >
      <option value={10}>10</option>
      <option value={20}>20</option>
      <option value={50}>50</option>
      <option value={100}>100</option>
    </select>

    {/* Previous */}
    <button
      type="button"
      className="btn-og btn-og-secondary btn-og-sm"
      onClick={handlePreviousPage}
      disabled={currentPage === 1 || loading}
      style={{
        minWidth: 75,
      }}
    >
      <i className="fa fa-chevron-left"></i>{" "}
      Previous
    </button>

    {/* Page Number */}
    <span
      style={{
        minWidth: 80,
        textAlign: "center",
        color: "#4a5568",
      }}
    >
      Page <strong>{currentPage}</strong> of{" "}
      <strong>{totalPages || 1}</strong>
    </span>

    {/* Next */}
    <button
      type="button"
      className="btn-og btn-og-primary btn-og-sm"
      onClick={handleNextPage}
      disabled={
        currentPage >= totalPages || loading || totalRecords === 0
      }
      style={{
        minWidth: 60,
      }}
    >
      Next{" "}
      <i className="fa fa-chevron-right"></i>
    </button>
  </div>
</div>
          </div>
        </div>
      </div>

      {loading ? (
        <Suspense>
          <ComponentLoader />
        </Suspense>
      ) : (
        ""
      )}
    </div>
  );
};

export default PMSSkipLevelList;
